import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Button, Form, InputGroup, Pagination, Alert, Badge } from 'react-bootstrap';
import { FaPlus, FaSearch, FaEdit, FaTrash, FaUserShield } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import Spinner from '../../components/layout/Spinner';
import AuthContext from '../../context/AuthContext';

const UserList = () => {
  const { user: currentUser, isAdmin } = useContext(AuthContext);
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    // Check if user is admin, if not redirect
    if (!isAdmin()) {
      toast.error('Access denied. Admin privileges required.');
      window.location.href = '/dashboard';
      return;
    }
    
    fetchUsers();
  }, [currentPage, sortField, sortOrder]);

  const fetchUsers = async (search = searchTerm) => {
    try {
      setLoading(true);
      const res = await axios.get('/api/users', {
        params: {
          page: currentPage,
          limit: 10,
          search,
          sortField,
          sortOrder
        }
      });
      
      setUsers(res.data.users);
      setTotalPages(res.data.pagination.pages);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers(searchTerm);
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const confirmDelete = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    
    try {
      await axios.delete(`/api/users/${userToDelete._id}`);
      toast.success(`User ${userToDelete.name} deleted successfully`);
      fetchUsers(); // Refresh the list
    } catch (err) {
      console.error('Error deleting user:', err);
      toast.error(err.response?.data?.msg || 'Failed to delete user');
    }
    
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleRoleToggle = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    
    try {
      await axios.put(`/api/users/${userId}/role`, { role: newRole });
      toast.success(`User role updated to ${newRole}`);
      fetchUsers(); // Refresh the list
    } catch (err) {
      console.error('Error updating user role:', err);
      toast.error(err.response?.data?.msg || 'Failed to update user role');
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge bg="danger">Admin</Badge>;
      case 'user':
        return <Badge bg="primary">User</Badge>;
      default:
        return <Badge bg="secondary">{role}</Badge>;
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    // Previous button
    pages.push(
      <Pagination.Item 
        key="prev" 
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
      >
        &laquo;
      </Pagination.Item>
    );
    
    // First page
    if (startPage > 1) {
      pages.push(
        <Pagination.Item 
          key={1} 
          active={1 === currentPage}
          onClick={() => handlePageChange(1)}
        >
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        pages.push(<Pagination.Ellipsis key="ellipsis1" />);
      }
    }
    
    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Pagination.Item 
          key={i} 
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    
    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<Pagination.Ellipsis key="ellipsis2" />);
      }
      pages.push(
        <Pagination.Item 
          key={totalPages} 
          active={totalPages === currentPage}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }
    
    // Next button
    pages.push(
      <Pagination.Item 
        key="next" 
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
      >
        &raquo;
      </Pagination.Item>
    );
    
    return <Pagination>{pages}</Pagination>;
  };

  if (loading && users.length === 0) {
    return <Spinner />;
  }

  return (
    <Container>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1>User Management</h1>
        </Col>
        <Col xs="auto">
          <Button as={Link} to="/users/new" variant="primary">
            <FaPlus className="me-2" /> Add New User
          </Button>
        </Col>
      </Row>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Card className="mb-4">
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <Form onSubmit={handleSearch}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search by name or email"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button type="submit" variant="primary">
                    <FaSearch /> Search
                  </Button>
                </InputGroup>
              </Form>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-4">
              <p className="mb-3">No users found.</p>
              <Button as={Link} to="/users/new" variant="primary">
                Add New User
              </Button>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                        Name {renderSortIcon('name')}
                      </th>
                      <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                        Email {renderSortIcon('email')}
                      </th>
                      <th>Role</th>
                      <th onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer' }}>
                        Created At {renderSortIcon('createdAt')}
                      </th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user._id}>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>{getRoleBadge(user.role)}</td>
                        <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                        <td className="text-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            title={user.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                            onClick={() => handleRoleToggle(user._id, user.role)}
                            disabled={user._id === currentUser._id} // Can't change own role
                          >
                            <FaUserShield />
                          </Button>
                          <Button
                            as={Link}
                            to={`/users/edit/${user._id}`}
                            variant="outline-secondary"
                            size="sm"
                            className="me-1"
                            title="Edit User"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            title="Delete User"
                            onClick={() => confirmDelete(user)}
                            disabled={user._id === currentUser._id} // Can't delete yourself
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              
              <div className="d-flex justify-content-center mt-4">
                {renderPagination()}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
      
      {/* Delete Confirmation Modal */}
      {userToDelete && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete user <strong>{userToDelete.name}</strong>?</p>
                <p className="text-danger">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default UserList;
